"use client"

import { useState, useCallback } from "react"
import { RefreshCw, ChevronRight } from "lucide-react"
import { trackProductEvent } from "@/lib/analytics/client"
import styles from "./choice-tool.module.css"

interface Question {
  optionA: string
  optionB: string
}

const questions: Question[] = [
  // Food choices
  { optionA: "Only eat pizza for the rest of your life", optionB: "Only eat sushi for the rest of your life" },
  { optionA: "Never eat chocolate again", optionB: "Never eat cheese again" },
  { optionA: "Only drink water forever", optionB: "Only drink juice forever" },
  { optionA: "Eat a raw onion every morning", optionB: "Drink a glass of hot sauce every evening" },
  { optionA: "Have unlimited free food at restaurants", optionB: "Have unlimited free home-delivered meals" },
  { optionA: "Only eat breakfast foods for every meal", optionB: "Never eat breakfast foods again" },
  { optionA: "Give up coffee forever", optionB: "Give up desserts forever" },
  { optionA: "Always have to cook your own meals", optionB: "Never be able to cook again" },

  // Superpowers
  { optionA: "Be able to fly", optionB: "Be able to read minds" },
  { optionA: "Have super strength", optionB: "Have super speed" },
  { optionA: "Be invisible whenever you want", optionB: "Be able to teleport anywhere" },
  { optionA: "Have the ability to talk to animals", optionB: "Have the ability to speak every language" },
  { optionA: "Control time (pause, rewind, fast-forward)", optionB: "Control the weather" },
  { optionA: "Have X-ray vision", optionB: "Have night vision" },
  { optionA: "Be able to breathe underwater", optionB: "Be able to survive in space" },
  { optionA: "Have perfect memory", optionB: "Have the ability to forget anything on command" },

  // Travel
  { optionA: "Travel to every country in the world", optionB: "Have a luxury home in your dream location" },
  { optionA: "Live on a tropical island", optionB: "Live in a cozy mountain cabin" },
  { optionA: "Only travel by boat for the rest of your life", optionB: "Only travel by train for the rest of your life" },
  { optionA: "Explore the deep ocean", optionB: "Explore outer space" },
  { optionA: "Live in the past (any era you choose)", optionB: "Live 100 years in the future" },
  { optionA: "Have a free first-class flight anywhere once a month", optionB: "Have a free 5-star hotel stay anywhere once a month" },
  { optionA: "Visit every national park in the world", optionB: "Visit every capital city in the world" },

  // Social situations
  { optionA: "Always have to say what's on your mind", optionB: "Never be able to speak again" },
  { optionA: "Have 1 best friend you can always count on", optionB: "Have 100 casual friends" },
  { optionA: "Be famous but have no privacy", optionB: "Be unknown but completely free" },
  { optionA: "Always be overdressed", optionB: "Always be underdressed" },
  { optionA: "Be the funniest person in every room", optionB: "Be the smartest person in every room" },
  { optionA: "Have everyone always agree with you", optionB: "Always hear honest opinions from everyone" },
  { optionA: "Never be able to use social media again", optionB: "Never be able to watch TV or movies again" },

  // Hypothetical scenarios
  { optionA: "Win $1 million right now", optionB: "Have a guaranteed $5,000/month for life" },
  { optionA: "Know when you're going to die", optionB: "Know how you're going to die" },
  { optionA: "Relive the same day forever (a good day)", optionB: "Live every day differently but never go back" },
  { optionA: "Have a rewind button for your life", optionB: "Have a pause button for your life" },
  { optionA: "Be the best player on a losing team", optionB: "Be the worst player on a winning team" },
  { optionA: "Never have to sleep again", optionB: "Never have to eat again" },
  { optionA: "Have all traffic lights turn green for you", optionB: "Never have to wait in a queue again" },
  { optionA: "Live without music", optionB: "Live without movies" },
  { optionA: "Always know when someone is lying", optionB: "Always get away with lying" },
  { optionA: "Be 10 years older than you are", optionB: "Be 10 years younger than you are" },

  // Fun & quirky
  { optionA: "Have a pet dinosaur", optionB: "Have a pet dragon" },
  { optionA: "Be able to talk to plants", optionB: "Be able to talk to computers" },
  { optionA: "Have a personal chef", optionB: "Have a personal trainer" },
  { optionA: "Only wear one color for the rest of your life", optionB: "Have to wear a different outfit every single day (never repeating)" },
  { optionA: "Have a photographic memory", optionB: "Be able to learn any skill in a day" },
  { optionA: "Live in a world without internet", optionB: "Live in a world without air conditioning/heating" },
  { optionA: "Be a famous musician", optionB: "Be a famous actor" },
  { optionA: "Have your dream job with average pay", optionB: "Have a boring job with amazing pay" },
  { optionA: "Never get stuck in traffic again", optionB: "Never have a slow internet connection again" },
  { optionA: "Be able to change your hair color at will", optionB: "Be able to change your eye color at will" },
  { optionA: "Always have the perfect comeback", optionB: "Always make the perfect first impression" },
  { optionA: "Live in a treehouse", optionB: "Live in a houseboat" },
  { optionA: "Have free Wi-Fi everywhere you go", optionB: "Have free coffee everywhere you go" },
  { optionA: "Be able to control your dreams", optionB: "Never need to dream again" },
  { optionA: "Always find a parking spot instantly", optionB: "Always have your phone fully charged" },
]

export function WouldYouRatherGame() {
  // Keep the first server and client render identical. Later questions can be random.
  const [currentIndex, setCurrentIndex] = useState(0)
  const [chosen, setChosen] = useState<"A" | "B" | null>(null)
  const [answered, setAnswered] = useState(0)
  const [usedIndices, setUsedIndices] = useState<Set<number>>(new Set())

  const question = questions[currentIndex]

  const handleChoice = useCallback(
    (choice: "A" | "B") => {
      if (chosen) return
      if (answered === 0) {
        trackProductEvent("tool_started", {
          properties: { tool_slug: "would-you-rather" },
        })
      }
      trackProductEvent("tool_completed", {
        properties: {
          tool_slug: "would-you-rather",
          interaction_type: "choice",
          choice,
          question_index: currentIndex,
          interactions_completed: answered + 1,
        },
      })
      setChosen(choice)
      setAnswered((prev) => prev + 1)
    },
    [answered, chosen, currentIndex]
  )

  const nextQuestion = useCallback(() => {
    const newUsed = new Set(usedIndices)
    newUsed.add(currentIndex)

    // Reset if all questions have been used
    if (newUsed.size >= questions.length) {
      newUsed.clear()
    }

    let nextIdx: number
    do {
      nextIdx = Math.floor(Math.random() * questions.length)
    } while (newUsed.has(nextIdx) && newUsed.size < questions.length)

    setUsedIndices(newUsed)
    setCurrentIndex(nextIdx)
    setChosen(null)
  }, [currentIndex, usedIndices])

  const reset = () => {
    setUsedIndices(new Set())
    setCurrentIndex(Math.floor(Math.random() * questions.length))
    setChosen(null)
    setAnswered(0)
  }

  return (
    <div className={styles.tool}>
      <div className={styles.topbar}>
        <p className={styles.eyebrow}>Would you rather...</p>
        {answered > 0 ? (
          <button onClick={reset} className={styles.reset}>
            <RefreshCw aria-hidden="true" />
            Reset {answered}
          </button>
        ) : (
          <span className={styles.counter}>Pick one</span>
        )}
      </div>

      <div className={styles.choices}>
        <button
          onClick={() => handleChoice("A")}
          disabled={!!chosen}
          className={`${styles.choice} ${styles.choiceA} ${
            chosen === "A" ? styles.selected : chosen === "B" ? styles.muted : ""
          }`}
        >
          <span className={styles.choiceLabel}>Option A</span>
          <span className={styles.choiceText}>{question.optionA}</span>
          <span className={styles.choiceHint}>
            {chosen === "A" ? "Your choice" : "Tap to choose"}
          </span>
        </button>

        <button
          onClick={() => handleChoice("B")}
          disabled={!!chosen}
          className={`${styles.choice} ${styles.choiceB} ${
            chosen === "B" ? styles.selected : chosen === "A" ? styles.muted : ""
          }`}
        >
          <span className={styles.choiceLabel}>Option B</span>
          <span className={styles.choiceText}>{question.optionB}</span>
          <span className={styles.choiceHint}>
            {chosen === "B" ? "Your choice" : "Tap to choose"}
          </span>
        </button>
      </div>

      {chosen && (
        <div className={styles.result} aria-live="polite">
          <p>
            <strong>Locked in.</strong> Ask everyone else, then compare answers.
          </p>
          <button
            onClick={nextQuestion}
            className={styles.next}
          >
            Next question
            <ChevronRight aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  )
}
